import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPlugin, createPlugin, updatePlugin, isRegistryOwnedPlugin } from '../lib/api';
import type { Plugin } from '../lib/api';

const CATS  = ['provider','analyzer','condition','hook','updater','generator','packager','publisher'];
const LICS  = ['Apache-2.0','MIT','GPL-3.0','BSD-2-Clause','BSD-3-Clause','MPL-2.0'];

export default function PluginEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew  = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState<Partial<Plugin>>({ namespace:'', name:'', description:'', author:'', category:'provider', repository:'', license:'Apache-2.0', tags:[] });
  const [tagsInput, setTagsInput] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const isRegistryOwned = isRegistryOwnedPlugin(form);

  useEffect(() => {
    if (!isNew && id) {
      getPlugin(id).then(({ data }) => { setForm(data); setTagsInput((data.tags ?? []).join(', ')); setLoading(false); })
        .catch((e: unknown) => { setError(e instanceof Error ? e.message : 'Failed'); setLoading(false); });
    }
  }, [id, isNew]);

  const set = (f: keyof Plugin, v: string | string[]) => setForm(p => ({ ...p, [f]: v }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError('');
    const namespace = form.namespace?.trim().toLowerCase() ?? '';
    if (namespace === '@semrel' || isRegistryOwned) {
      setError('@semrel plugins are maintained through SQL migrations and cannot be changed in the UI.');
      setSaving(false);
      return;
    }
    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
    try {
      isNew ? await createPlugin({ ...form, tags }) : await updatePlugin(id!, { ...form, tags });
      navigate('/admin/plugins');
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Save failed'); setSaving(false); }
  }

  if (loading) return <div className="page__body muted">Loading…</div>;

  return (
    <>
      <div className="page__header">
        <h1 className="page__title">{isNew ? 'Add Plugin' : `Edit · ${form.name}`}</h1>
        <button type="button" className="btn btn--sm" onClick={() => navigate('/admin/plugins')}>← Back</button>
      </div>
      <div className="page__body">
        {error && <div className="alert alert--error">{error}</div>}
        {isRegistryOwned && (
          <div className="alert alert--info">
            <strong>This plugin is owned by SemRels.</strong> Its metadata is maintained through SQL migrations; edit and save are disabled here.
          </div>
        )}
        <div className="card max-w-640 mx-auto">
          <form onSubmit={(e) => { void handleSubmit(e); }}>
            <fieldset className="plugin-edit__fields" disabled={saving || isRegistryOwned}>
            <div className="form-grid-3">
              <div className="field">
                <label htmlFor="namespace">Namespace</label>
                <input id="namespace" className="input" value={form.namespace ?? ''} onChange={e => set('namespace', e.target.value)} placeholder="@your-org" aria-describedby="namespace-help" />
                <span id="namespace-help" className="muted text-xs">The @semrel namespace is reserved for migration-managed plugins.</span>
              </div>
              <div className="field">
                <label htmlFor="name">Name *</label>
                <input id="name" className="input" value={form.name ?? ''} onChange={e => set('name', e.target.value)} placeholder="provider-github" required disabled={!isNew} />
              </div>
              <div className="field">
                <label htmlFor="category">Category *</label>
                <select id="category" className="select" value={form.category ?? 'provider'} onChange={e => set('category', e.target.value)}>
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="desc">Description *</label>
              <textarea id="desc" className="textarea" value={form.description ?? ''} onChange={e => set('description', e.target.value)} placeholder="Short description" required />
            </div>
            <div className="form-grid-2">
              <div className="field">
                <label htmlFor="author">Author *</label>
                <input id="author" className="input" value={form.author ?? ''} onChange={e => set('author', e.target.value)} placeholder="semrel Authors" required />
              </div>
              <div className="field">
                <label htmlFor="license">License *</label>
                <select id="license" className="select" value={form.license ?? 'Apache-2.0'} onChange={e => set('license', e.target.value)}>
                  {LICS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="repo">Repository URL</label>
              <input id="repo" type="url" className="input" value={form.repository ?? ''} onChange={e => set('repository', e.target.value)} placeholder="https://github.com/SemRels/…" />
            </div>
            <div className="field">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input id="tags" className="input" value={tagsInput} onChange={e => setTagsInput(e.target.value)} placeholder="github, provider" />
            </div>
            </fieldset>
            <div className="flex gap-sm mt-1">
              {!isRegistryOwned && <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : isNew ? 'Create' : 'Save'}</button>}
              <button type="button" className="btn" onClick={() => navigate('/admin/plugins')}>Back to plugins</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
